import { useMemo, useState } from 'react';
import { ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { Typography } from '@/components/ui/typography';
import { DataTablePagination } from '@/components/ui/data-table-pagination';
import { TableRowActions } from '@/components/ui/table-row-actions';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { BELOW_DESKTOP_QUERY } from '@/config/ui.constants';
import { cn } from '@/lib/utils';
import { formatDate } from '@/util/formatting';
import dayjs from 'dayjs';

export type {
  Columns,
  TableConfiguration,
  Filters,
  ActionList,
  PageStep,
  CheckBoxState,
  ComponentData,
  PaginationConfiguration,
} from '@/types/table';
export { filterTypes } from '@/types/table';

import type {
  Columns,
  TableConfiguration,
  Filters,
  ActionList,
  PageStep,
  TableRowData,
  SortState,
} from '@/types/table';
import { filterTypes } from '@/types/table';

/**
 * Comparator for a single column's cell values, honoring the column's
 * declared `type` (component cells unwrap to their `.data` scalar first).
 * Pure — does not read or write `column.asc`; direction is passed in.
 */
function compareCells<TRow extends TableRowData>(
  a: TRow,
  b: TRow,
  column: Columns,
  asc: boolean,
): number {
  let nameA: unknown = a[column.selector];
  let nameB: unknown = b[column.selector];

  if (column.type === 'date' || column.type === 'dateTime') {
    nameA = new Date(nameA as string | number | Date).getTime();
    nameB = new Date(nameB as string | number | Date).getTime();
  }
  if (column.type === 'text') {
    nameA = (nameA as { toString: () => string }).toString().toUpperCase();
    nameB = (nameB as { toString: () => string }).toString().toUpperCase();
  }
  if (column.type === 'component') {
    if ((nameA as { data?: unknown })?.data !== undefined)
      nameA = (nameA as { data: unknown }).data;
    if ((nameB as { data?: unknown })?.data !== undefined)
      nameB = (nameB as { data: unknown }).data;
    if (column?.componentDataType === 'date') {
      nameA = new Date(nameA as string | number | Date).getTime();
      nameB = new Date(nameB as string | number | Date).getTime();
    } else {
      nameA = nameA?.toString().toUpperCase();
      nameB = nameB?.toString().toUpperCase();
    }
  }

  if ((nameA as never) < (nameB as never)) return asc ? -1 : 1;
  if ((nameA as never) > (nameB as never)) return asc ? 1 : -1;
  return 0;
}

/**
 * Renders one cell's value for a given column `type`. Module-scope and
 * exported so a consumer's `renderMobileCard` card can render a field exactly
 * as its table row does — including the absent-value placeholder, which a
 * card that reached for `formatDate` directly would render as "Invalid Date"
 * (the bug Unit 4 PR C hit on `uploadedDate`).
 */
export function renderCellValue(
  type: string | undefined,
  data: unknown,
): React.ReactNode {
  if (!data) return <span className="text-muted-foreground">-</span>;
  if (type === 'date') return formatDate(data as string | Date);
  if (type === 'dateTime')
    return dayjs(data as string | Date).format('MMM D, YYYY h:mm A');
  if (type === 'component') return (data as { component: React.ReactNode }).component;
  return (data as { toString: () => string }).toString().replace(/,/g, ', ');
}

/**
 * Full-featured data table driven by column metadata: client- or server-side sorting/filtering/pagination (optionally seeded with an initial sort, page and page size), sticky columns, row selection, and emptyState/noResultsState slots. Below 1024px, rows render through the opt-in `renderMobileCard` renderer as cards instead of a `<table>`.
 *
 * @category Data
 */
function DataTable<TRow extends TableRowData = TableRowData>({
  columns,
  tableData,
  filters,
  isLoading,
  actionList,
  onButtonClick,
  showCheckboxes,
  selection,
  onColumnIconClick,
  actionBar,
  onServerPageChange,
  onSort,
  initialSort = null,
  initialPage,
  initialPageSize,
  totalRowsTitle,
  hideActionBar = false,
  hidePagination = false,
  isServerSide = false,
  totalAvailableRows = 1,
  emptyState,
  noResultsState,
  renderMobileCard,
  sortState: controlledSortState,
}: TableConfiguration<TRow>) {
  // Called unconditionally (Rules of Hooks); consumed only when the slot
  // (renderMobileCard) is also provided.
  const isBelowLg = useMediaQuery(BELOW_DESKTOP_QUERY);
  const useCards = isBelowLg && !!renderMobileCard;
  const [internalSortState, setInternalSortState] = useState<SortState | null>(initialSort);
  // Controlled when the consumer passes `sortState` (null included); otherwise
  // the click-driven state DataTable has always kept, seeded by `initialSort`
  // for a list restoring where the user left it (DV-3455).
  const sortState =
    controlledSortState !== undefined ? controlledSortState : internalSortState;
  const [pageStep, setPageStep] = useState<PageStep>({
    lowerIndex: 0,
    upperIndex: 10,
  });

  const isValid = (filterBuffer: Filters, cellData: TRow) => {
    const raw = cellData[filterBuffer.selector];
    const unwrapped =
      raw !== null && typeof raw === 'object' && 'data' in raw
        ? (raw as { data: unknown }).data
        : raw;
    const dataBuffer = unwrapped?.toString().toLowerCase();
    if (filterBuffer?.criteria === filterTypes.EQUALS) {
      return dataBuffer === filterBuffer.value.toString().toLowerCase();
    }
    return dataBuffer?.includes(
      filterBuffer.value.toString().toLowerCase(),
    );
  };

  const filteredData = useMemo(() => {
    if (hidePagination) return tableData;
    return tableData.filter((data) => {
      for (const filterBuffer of filters) {
        if (filterBuffer.value && !isValid(filterBuffer, data)) return false;
      }
      return true;
    });
    // `isValid` reads only its arguments (no external mutable state), so
    // it's excluded from deps safely.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableData, filters, hidePagination]);

  const sortedData = useMemo(() => {
    if (!sortState || isServerSide) return filteredData;
    const column = columns.find((c) => c.selector === sortState.selector);
    if (!column) return filteredData;
    return [...filteredData].sort((a, b) => compareCells(a, b, column, sortState.asc));
  }, [filteredData, sortState, columns, isServerSide]);

  const onPageChange = (
    upperIndex: number,
    lowerIndex: number,
    activeIndex: number,
    rowsPerPage: number,
  ) => {
    if (!hidePagination) {
      if (isServerSide) {
        // Selection clearing is the consumer's call (see PropertyListPage's
        // onServerPageChange wiring). DataTable doesn't know whether this
        // call is a real page change or a remount from a view switch — the
        // consumer's skipInitialPaginationRef gates both API fetch and
        // selection.clear() together.
        onServerPageChange?.(activeIndex, rowsPerPage);
        setPageStep({ upperIndex: 0, lowerIndex: rowsPerPage });
      } else {
        setPageStep({ upperIndex, lowerIndex });
      }
    }
  };

  // Shared with `sortableColumns` below (the mobile sort menu's option list)
  // so the menu never offers a column that clicking the header wouldn't
  // actually sort. `isSortableByHeader === true` always wins; otherwise a
  // column is sortable only when its `type` is one `compareCells` knows how
  // to order, and only when it hasn't been explicitly opted out.
  const isSortable = (column: Columns) =>
    column.isSortableByHeader === true ||
    (column.isSortableByHeader !== false &&
      ['text', 'number', 'date', 'dateTime'].includes(column.type ?? ''));

  const performSort = (column: Columns) => {
    if (isSortable(column)) sort(column);
  };

  const sort = (column: Columns) => {
    const nextAsc = sortState?.selector === column.selector ? !sortState.asc : true;
    setInternalSortState({ selector: column.selector, asc: nextAsc });
    if (isServerSide) onSort?.(column.selector, nextAsc);
  };

  const sortIcon = (column: Columns) => {
    const isActive = sortState?.selector === column.selector;
    if (isActive && sortState?.asc)
      return <ArrowUp className="h-4 w-4 group-hover/sortable:text-primary" />;
    if (isActive && sortState?.asc === false)
      return <ArrowDown className="h-4 w-4 group-hover/sortable:text-primary" />;
    if (column.isSortableByHeader !== false)
      return (
        <ArrowUpDown className="h-4 w-4 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 group-hover/sortable:text-primary" />
      );
    return null;
  };

  const ariaSort = (column: Columns): 'ascending' | 'descending' | undefined => {
    if (sortState?.selector !== column.selector) return undefined;
    return sortState.asc ? 'ascending' : 'descending';
  };

  const isDataLoading = () => isLoading || sortedData.length === 0;

  const dataSlice = hidePagination
    ? sortedData
    : sortedData.slice(pageStep.upperIndex, pageStep.lowerIndex);

  // Sticky cells need three things the non-sticky <TableCell>/<TableHead>
  // primitives don't provide:
  //  1. A combined shadow that bundles the column-divider with the bottom
  //     row-hairline (otherwise tailwind-merge dedupes two `shadow-*`
  //     utilities and drops the row separator on sticky cells).
  //  2. A z-index so the cell paints above non-sticky siblings.
  // Z-index hierarchy:
  //  - body non-sticky cells: z-auto (default, ~0)
  //  - body sticky-left/right (z-[10]): paint above body siblings during
  //    horizontal scroll. Below header cells.
  //  - header non-sticky (primitive's z-10): standard sticky-top behavior.
  //  - header sticky-left/right corners (z-[20]): must beat the primitive's
  //    z-10 on sibling header cells, otherwise non-sticky mid-headers paint
  //    on top of the pinned Address/moreOption headers and you see e.g.
  //    "AVAILABLE DATE" where "ADDRESS" should be after horizontal scroll.
  // Hover and selected backgrounds come from the <TableCell> primitive's
  // opaque tokens — sticky cells inherit them and stay visually identical
  // to non-sticky siblings during hover.
  const stickyClass = (
    sticky: 'left' | 'right' | undefined,
    isHeader: boolean,
  ) => {
    const zIndex = isHeader ? 'z-[20]' : 'z-[10]';
    if (sticky === 'left') {
      return cn(
        'sticky left-0 shadow-[1px_0_0_var(--border),inset_0_-1px_0_var(--border)]',
        zIndex,
      );
    }
    if (sticky === 'right') {
      return cn(
        'sticky right-0 shadow-[-1px_0_0_var(--border),inset_0_-1px_0_var(--border)]',
        zIndex,
      );
    }
    return undefined;
  };

  const firstColumnSticky = columns[0]?.sticky === 'left';
  const pinCheckboxLeft = showCheckboxes && firstColumnSticky;
  // When the checkbox column is pinned alongside the first user column, the
  // user column's `left` must shift past the checkbox so they don't stack.
  // 40px matches the explicit width set on the checkbox <th>/<td> below.
  const CHECKBOX_COL_PX = 40;

  // Multiple pinned-right columns (e.g. an Add-to-Cart action beside the
  // row-actions kebab) would all resolve to `right: 0` and overlap. For the
  // contiguous run of sticky-right columns at the end, give each a `right`
  // offset equal to the summed widths of the sticky-right columns to its
  // right. The rightmost stays at 0 (handled by the `right-0` class).
  // Engages only when 2+ trailing sticky-right columns exist, so single
  // pinned-right tables keep their exact previous layout.
  const trailingStickyRight = (() => {
    let n = 0;
    for (let i = columns.length - 1; i >= 0 && columns[i]?.sticky === 'right'; i--) n++;
    return n;
  })();
  const multiStickyRight = trailingStickyRight > 1;
  const rightOffsets = (() => {
    const offsets = new Array(columns.length).fill(0);
    if (!multiStickyRight) return offsets;
    let acc = 0;
    for (let i = columns.length - 1; i >= 0 && columns[i]?.sticky === 'right'; i--) {
      offsets[i] = acc;
      acc += parseInt(String(columns[i]?.width ?? '0'), 10) || 0;
    }
    return offsets;
  })();

  const masterCheckboxState = selection?.isIndeterminate
    ? 'indeterminate'
    : (selection?.isAllSelected ?? false);

  const sortableColumns = columns.filter((c) => isSortable(c) && c.name);

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {!hideActionBar && (showCheckboxes || actionBar) && (
        <div className="shrink-0 bg-background flex items-center gap-4 py-2 px-3 border-y border-border">
          {showCheckboxes && selection && (
            <>
              <Checkbox
                checked={masterCheckboxState}
                disabled={isDataLoading()}
                onCheckedChange={(checked) =>
                  selection.toggleAll(checked === true)
                }
              />
              <div className="flex flex-col">
                <Typography variant="body-medium">
                  {totalAvailableRows} {totalRowsTitle || ''}
                </Typography>
                <Typography variant="small">
                  {selection.selectedIds.size} Selected
                </Typography>
              </div>
            </>
          )}
          {actionBar && <div className="flex-1">{actionBar}</div>}
        </div>
      )}

      <div className="flex-1 min-h-0 overflow-auto">
        {isLoading && (
          <div className="flex items-center justify-center py-16">
            <Spinner size="lg" />
          </div>
        )}
        {!isLoading && sortedData.length === 0 && (
          <EmptyBody
            tableHasData={tableData.length > 0}
            emptyState={emptyState}
            noResultsState={noResultsState}
          />
        )}

        {!isDataLoading() && useCards && (
          <>
            {sortableColumns.length > 0 && (
              <div className="flex justify-end px-4 pt-3">
                <TableRowActions
                  title="Sort By"
                  data={{ id: 'mobile-card-sort' }}
                  options={sortableColumns.map((c) => ({
                    id: c.selector,
                    text: c.name,
                    key: c.selector,
                    active: sortState?.selector === c.selector,
                  }))}
                  onMoreOptionClick={(option) => {
                    const column = columns.find((c) => c.selector === option.key);
                    if (column) performSort(column);
                  }}
                >
                  <Button variant="outline" size="sm" className="gap-1.5">
                    <ArrowUpDown className="h-4 w-4" />
                    {sortState
                      ? `${columns.find((c) => c.selector === sortState.selector)?.name ?? 'Sort'} ${sortState.asc ? '↑' : '↓'}`
                      : 'Sort'}
                  </Button>
                </TableRowActions>
              </div>
            )}
            <ul className="flex flex-col gap-3 p-4" data-testid="data-table-card-list">
              {dataSlice.map((data) => {
                const rowId = String(data.id ?? '');
                return (
                  <li key={rowId} className="flex items-start gap-2">
                    {showCheckboxes && selection && (
                      <Checkbox
                        className="mt-1"
                        checked={selection.isSelected(rowId)}
                        onCheckedChange={(checked) =>
                          selection.toggleRow(rowId, checked === true)
                        }
                        aria-label="Select row"
                      />
                    )}
                    <div className="min-w-0 flex-1">{renderMobileCard!(data)}</div>
                  </li>
                );
              })}
            </ul>
          </>
        )}
        {!isDataLoading() && !useCards && (
          <Table>
            <TableHeader>
              <TableRow>
                {showCheckboxes && (
                  <TableHead
                    style={{
                      width: `${CHECKBOX_COL_PX}px`,
                      minWidth: `${CHECKBOX_COL_PX}px`,
                    }}
                    className={cn(pinCheckboxLeft && stickyClass('left', true))}
                  />
                )}
                {columns.map((column, index) => (
                  <TableHead
                    key={column.selector}
                    onClick={() => performSort(column)}
                    aria-sort={ariaSort(column)}
                    style={{
                      minWidth: column.width,
                      width:
                        column.sticky === 'right' && multiStickyRight
                          ? column.width
                          : columns.length === index + 1
                            ? undefined
                            : column.width,
                      ...(column.sticky === 'left' &&
                      index === 0 &&
                      pinCheckboxLeft
                        ? { left: `${CHECKBOX_COL_PX}px` }
                        : {}),
                      ...(rightOffsets[index]
                        ? { right: `${rightOffsets[index]}px` }
                        : {}),
                    }}
                    className={cn(
                      'group',
                      column.cssClass,
                      column.isSortableByHeader !== false &&
                        'cursor-pointer select-none group/sortable hover:bg-muted',
                      column.type === 'number' && 'text-right',
                      stickyClass(column.sticky, true),
                    )}
                  >
                    <div
                      className={cn(
                        'flex items-center gap-1 whitespace-nowrap',
                        column.type === 'number' && 'justify-end',
                      )}
                    >
                      <Typography
                        variant="small-semibold"
                        className="uppercase tracking-wider text-foreground group-hover/sortable:text-primary"
                      >
                        {column.name}
                      </Typography>
                      {column.isMandatory && (
                        <span className="text-destructive">*</span>
                      )}
                      {(() => {
                        if (
                          column.icon === undefined ||
                          column.icon === null ||
                          column.icon === false
                        ) {
                          return sortIcon(column);
                        }
                        const direction = (column.icon as {
                          direction?: string;
                        })?.direction;
                        const iconNode =
                          (column.icon as { icon?: React.ReactNode })?.icon ??
                          (column.icon as React.ReactNode);
                        const iconButton = (
                          <Button
                            variant="ghost"
                            size="icon"
                            className={cn(
                              'h-6 w-6',
                              direction === 'right' && 'ml-auto',
                            )}
                            onClick={(e) => {
                              e.stopPropagation();
                              onColumnIconClick?.({ ...column });
                            }}
                          >
                            {iconNode}
                          </Button>
                        );
                        // direction='right' anchors the icon button to the
                        // far edge of the header, sort indicator stays glued
                        // to the column name. Any other value keeps the
                        // legacy ordering (icon adjacent to name, sort last).
                        return direction === 'right' ? (
                          <>
                            {sortIcon(column)}
                            {iconButton}
                          </>
                        ) : (
                          <>
                            {iconButton}
                            {sortIcon(column)}
                          </>
                        );
                      })()}
                    </div>
                  </TableHead>
                ))}
                {actionList && <TableHead className="w-20" />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {dataSlice.map((data) => {
                // Selection's RowSelection contract is string-keyed; rows
                // typed as `id?: string | number` get coerced here. Rows
                // without ids never participate in selection (showCheckboxes
                // off), so the empty-string fallback is unreachable in that
                // path.
                const rowId = String(data.id ?? '');
                return (
                  <TableRow
                    key={rowId}
                    data-state={
                      selection?.isSelected(rowId) ? 'selected' : undefined
                    }
                  >
                    {showCheckboxes && selection && (
                      <TableCell
                        style={{
                          width: `${CHECKBOX_COL_PX}px`,
                          minWidth: `${CHECKBOX_COL_PX}px`,
                        }}
                        className={cn(
                          'align-middle',
                          pinCheckboxLeft && stickyClass('left', false),
                        )}
                      >
                        <Checkbox
                          checked={selection.isSelected(rowId)}
                          onCheckedChange={(checked) =>
                            selection.toggleRow(rowId, checked === true)
                          }
                        />
                      </TableCell>
                    )}
                  {columns.map((column, index) => (
                    <TableCell
                      key={`${column.selector}${data.id}`}
                      style={{
                        ...(column.sticky === 'left' &&
                        index === 0 &&
                        pinCheckboxLeft
                          ? { left: `${CHECKBOX_COL_PX}px` }
                          : {}),
                        ...(column.sticky === 'right' && multiStickyRight
                          ? { width: column.width, minWidth: column.width }
                          : {}),
                        ...(rightOffsets[index]
                          ? { right: `${rightOffsets[index]}px` }
                          : {}),
                      }}
                      className={cn(
                        column.isBold && 'font-semibold',
                        column.type === 'number' && 'text-right tabular-nums',
                        stickyClass(column.sticky, false),
                      )}
                    >
                      {renderCellValue(column.type, data[column.selector])}
                    </TableCell>
                  ))}
                    {actionList && (
                      <TableCell>
                        <div className="flex gap-1">
                          {actionList.map((action: ActionList) => (
                            <Button
                              key={action.key}
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => onButtonClick?.(action.key, data)}
                            >
                              {(action.icon as { icon?: React.ReactNode })?.icon ??
                                (action.icon as React.ReactNode)}
                            </Button>
                          ))}
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>

      {!isDataLoading() && !hidePagination && (
        <div className="shrink-0 border-t border-border bg-background">
          <DataTablePagination
            totalRows={
              isServerSide ? totalAvailableRows : sortedData.length
            }
            initialPage={initialPage}
            initialRowsPerPage={initialPageSize}
            onPageChange={onPageChange}
          />
        </div>
      )}
    </div>
  );
}

function EmptyBody({
  tableHasData,
  emptyState,
  noResultsState,
}: {
  tableHasData: boolean;
  emptyState?: React.ReactNode;
  noResultsState?: React.ReactNode;
}) {
  const slot = tableHasData
    ? (noResultsState ?? emptyState)
    : emptyState;
  if (slot) return <>{slot}</>;
  return (
    <div className="flex items-center justify-center py-16">
      <Typography className="text-lg">No rows to show</Typography>
    </div>
  );
}

export { DataTable };
